import { format, isValid } from "date-fns";
import { NonEmptyArray } from "types";

const dayFormatter = new Intl.DateTimeFormat("en-AU", {
  timeZone: "UTC",
  weekday: "long",
  month: "long",
  day: "numeric",
});

export function formatDueDate(paymentDue: string | undefined): string {
  if (!paymentDue) {
    return "–";
  }

  const date = new Date(`${paymentDue}T00:00:00.000Z`);

  try {
    return dayFormatter.format(date).replace(",", ""); // UX people really insisted to remove the comma :)
  } catch (_) {
    console.error(`Invalid payment due date: ${paymentDue}`);
    return "–";
  }
}

export function formatMaskedMobile(maskedMobile: string): string {
  // String.fromCharCode(8226) === &bull; can't use html entity in string
  const mobileWithDots = formatMaskedValue(maskedMobile, true);
  // 10 digit numbers have specific spacing, otherwise space every 3 characters
  return mobileWithDots.length === 10
    ? `${mobileWithDots.slice(0, 3)} ${mobileWithDots.slice(
        3,
        6,
      )} ${mobileWithDots.slice(6)}`
    : mobileWithDots.replace(/(.{3})/g, "$1 ").trim();
}

/**
 * 🐨 Helper function to make sure an optional property in an
 * interface , actually has a value.
 * Ref: https://stackoverflow.com/questions/52127082/ensure-existance-of-optional-property-in-typescript-interface
 */
export type MakeRequired<T, K extends keyof T> = Pick<
  T,
  Exclude<keyof T, K>
> & { [P in K]-?: Exclude<T[P], undefined> };

export const checkFields = <T, K extends keyof T>(
  o: T | MakeRequired<T, K>,
  ...fields: K[]
): o is MakeRequired<T, K> => fields.every((f) => !!o[f]);

// See: https://stackoverflow.com/a/29858893/247243
const FIRST_LETTER_REGEX = /(\b[a-z](?!\s))/g;

export const capitaliseEveryWord = (str: string | undefined): string =>
  String(str)
    .toLowerCase()
    .replace(FIRST_LETTER_REGEX, (firstLetter) => firstLetter.toUpperCase());

export const isNonEmptyArray = <T>(arr?: T[]): arr is NonEmptyArray<T> =>
  (arr?.length ?? 0) > 0;

export const formatDate = (input = ""): string => {
  const date = new Date(input);
  if (!isValid(date)) {
    return "";
  }

  return format(date, "dd MMMM yyyy");
};

// don't want to install an npm package for this just adding the code into here.
// https://github.com/gregberge/react-merge-refs/blob/3dc2804807097562e9952cfa6057a8a3bef9f39f/src/index.tsx
export function mergeRefs<T>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>,
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

type Option = { label: string; value: string };
type Options = Option[] | Readonly<Option[]>;
type OptionsValues<T extends Options> = T[number]["value"] | "";

export const mapValueToLabel = <T extends Options>(
  value: OptionsValues<T>,
  options: T,
) => options.find((option) => option.value === value)?.label ?? "-";

export function formatMaskedValue(
  maskedValue: string,
  isPhoneNumber: boolean = false,
): string {
  if (isPhoneNumber) return maskedValue.replace(/[x*]/gi, "•");
  return maskedValue.replace(/[*]/g, "•");
}
